'use client';

import React, { useMemo } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import type { ListSelectorProps } from '../types';

/**
 * Displays a group of lists with selection functionality
 */
const ListSelector: React.FC<ListSelectorProps> = ({
  title,
  lists,
  selectedId,
  onSelect,
}) => {
  const orderedLists = useMemo(() => {
    return [...lists].sort((a, b) => a.name?.localeCompare(b.name));
  }, [lists]);

  return (
    <div className="flex flex-col gap-1">
      <div className="px-3 py-2 bg-neutral-100 rounded-lg">
        <h3 className="text-sm font-semibold text-neutral-700">{title}</h3>
      </div>
      {orderedLists.length === 0 ? (
        <div className="px-3 py-2 text-sm text-neutral-500">No lists found</div>
      ) : (
        orderedLists.map((list) => {
          const listIdStr = String(list.id);
          const isSelected = selectedId === listIdStr;
          return (
            <button
              key={list.id}
              type="button"
              onClick={() => onSelect(listIdStr)}
              className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg cursor-pointer transition-all text-left w-full ${
                isSelected
                  ? 'bg-sky-50 border-2 border-sky-500'
                  : 'border-2 border-transparent hover:bg-neutral-50'
              }`}
              data-testid={`list-option-${list.id}`}
            >
              <span
                className={`text-sm ${
                  isSelected
                    ? 'text-sky-600 font-semibold'
                    : 'text-neutral-900'
                }`}
              >
                {list.name}
              </span>
              {isSelected && (
                <Icon icon={faCheck} className="w-4 h-4 text-sky-600" />
              )}
            </button>
          );
        })
      )}
    </div>
  );
};

export default ListSelector;
